// ============================================================
// instanceKinds - talents and traits, as one thing with two faces.
//
// The chips and the picker treat a talent and a trait alike: an id, an
// optional parameter, a catalog to look it up in and a set of edits.
// This table is where the two are told apart, so neither component has
// to branch on which it was given.
// ============================================================
import type { Character, TalentInstance, TraitInstance } from '../../engine/character/types';
import { TALENT_CATALOG } from '../../engine/character/talentCatalog';
import { TRAIT_CATALOG } from '../../engine/character/traitCatalog';
import type { TalentEffect } from '../../engine/character/talentEffects';
import {
  addCustomTalent,
  addCustomTrait,
  addTalent,
  addTrait,
  canAddTalent,
  canAddTrait,
  removeTalentAt,
  removeTraitAt,
  setTalentAt,
  setTraitAt,
  type InstancePatch,
} from '../../engine/character/instanceEdits';
import { talentText, traitText, type InstanceText } from '../../engine/character/instanceText';
import { getGrantedTalents } from '../../engine/character/effectiveTalents';
import { ruleCoverage } from '../../engine/character/ruleCoverage';

export type InstanceKind = 'talent' | 'trait';

/** A catalog entry as the picker needs it, whichever catalog it is from. */
export interface CatalogEntry {
  id: string;
  name: string;
  description: string;
  /** The heading it is listed under. */
  group: string;
  parameterized: boolean;
  parameterLabel?: string;
  repeatable?: boolean;
  effects: TalentEffect[];
  /** The tag for an entry the engine applies in part or not at all; null when it applies all of it. */
  coverageTag: string | null;
}

/** One instance on a sheet, whichever kind: its id and what the sheet says about it. */
export interface Instance {
  id: string;
  parameter?: string;
  name?: string;
  note?: string;
}

interface KindApi {
  entries: CatalogEntry[];
  instancesOf: (character: Character) => Instance[];
  /** What something else on the sheet confers: shown, never removable. */
  grantedOf: (character: Character) => Instance[];
  textOf: (instance: Instance) => InstanceText | null;
  canAdd: (character: Character, id: string) => boolean;
  add: (character: Character, id: string) => Partial<Character>;
  addCustom: (character: Character, name: string) => Partial<Character>;
  removeAt: (character: Character, index: number) => Partial<Character>;
  setAt: (character: Character, index: number, patch: InstancePatch) => Partial<Character>;
}

const TALENT_GROUPS: Record<string, string> = {
  combat: 'Combat',
  defence: 'Defence',
  general: 'General',
  narrative: 'Narrative',
};

const COVERAGE_TAG = { applied: null, partial: 'rules in part', note: 'note only' } as const;

const talentEntries: CatalogEntry[] = Object.values(TALENT_CATALOG).map((def) => ({
  ...def,
  group: TALENT_GROUPS[def.category],
  coverageTag: COVERAGE_TAG[ruleCoverage(def)],
}));

const traitEntries: CatalogEntry[] = Object.values(TRAIT_CATALOG).map((def) => ({
  ...def,
  group: def.mechanical ? 'Mechanical' : 'Narrative',
  coverageTag: COVERAGE_TAG[ruleCoverage(def)],
}));

const asTalent = (i: Instance): TalentInstance => ({ ...i, talentId: i.id });
const asTrait = (i: Instance): TraitInstance => ({ ...i, traitId: i.id });
const fromTalent = ({ talentId, ...rest }: TalentInstance): Instance => ({ id: talentId, ...rest });
const fromTrait = ({ traitId, ...rest }: TraitInstance): Instance => ({ id: traitId, ...rest });

export const KINDS: Record<InstanceKind, KindApi> = {
  talent: {
    entries: talentEntries,
    instancesOf: (ch) => (ch.talents ?? []).map(fromTalent),
    grantedOf: (ch) => getGrantedTalents(ch).map(fromTalent),
    textOf: (i) => talentText(asTalent(i)),
    canAdd: canAddTalent,
    add: addTalent,
    addCustom: addCustomTalent,
    removeAt: removeTalentAt,
    setAt: setTalentAt,
  },
  trait: {
    entries: traitEntries,
    instancesOf: (ch) => (ch.traits ?? []).map(fromTrait),
    grantedOf: () => [],
    textOf: (i) => traitText(asTrait(i)),
    canAdd: canAddTrait,
    add: addTrait,
    addCustom: addCustomTrait,
    removeAt: removeTraitAt,
    setAt: setTraitAt,
  },
};

const BY_ID: Record<InstanceKind, Map<string, CatalogEntry>> = {
  talent: new Map(talentEntries.map((entry) => [entry.id, entry])),
  trait: new Map(traitEntries.map((entry) => [entry.id, entry])),
};

/** The catalog entry behind an id, or undefined for a custom or retired one. */
export function entryFor(kind: InstanceKind, id: string): CatalogEntry | undefined {
  return BY_ID[kind].get(id);
}
